import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  Button,
} from '@mui/material';
import {
  CheckCircleOutline as ApproveIcon,
  BlockOutlined as RejectIcon,
  EditOutlined as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import reviewQueueService, {
  ReviewEntityType,
  ReviewQueueItem,
  ReviewQueueSummary,
  REVIEW_TYPE_LABELS,
  REJECT_SUPPORTED,
  getEditUrl,
} from '../../../services/reviewQueueService';

// ── Type chip colours ─────────────────────────────────────────────────────────

const TYPE_COLOR: Record<ReviewEntityType, 'default' | 'primary' | 'secondary' | 'warning' | 'info' | 'success'> = {
  wine:      'primary',
  producer:  'secondary',
  region:    'info',
  grape:     'success',
  wine_name: 'warning',
};

// ── Key fields renderer ───────────────────────────────────────────────────────

const KeyFields: React.FC<{ fields: Record<string, unknown> }> = ({ fields }) => {
  const entries = Object.entries(fields).filter(([, v]) => v != null && v !== '');
  if (entries.length === 0) return <Typography variant="caption" color="text.secondary">—</Typography>;
  return (
    <Stack spacing={0} sx={{ lineHeight: 1.4 }}>
      {entries.map(([k, v]) => (
        <Typography key={k} variant="caption" color="text.secondary">
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {k.replace(/([A-Z])/g, ' $1').toLowerCase()}:{' '}
          </Box>
          {String(v)}
        </Typography>
      ))}
    </Stack>
  );
};

// ── Summary chips ─────────────────────────────────────────────────────────────

interface SummaryBarProps {
  summary: ReviewQueueSummary;
  activeFilter: ReviewEntityType | undefined;
  onFilter: (t: ReviewEntityType | undefined) => void;
}

const SummaryBar: React.FC<SummaryBarProps> = ({ summary, activeFilter, onFilter }) => {
  const theme = useTheme();
  const chips: { label: string; type: ReviewEntityType | undefined; count: number }[] = [
    { label: 'All',       type: undefined,    count: summary.total },
    { label: 'Wine',      type: 'wine',       count: summary.wine },
    { label: 'Producer',  type: 'producer',   count: summary.producer },
    { label: 'Region',    type: 'region',     count: summary.region },
    { label: 'Grape',     type: 'grape',      count: summary.grape },
    { label: 'Wine Name', type: 'wine_name',  count: summary.wine_name },
  ];

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {chips.map(({ label, type, count }) => {
        const active = activeFilter === type;
        return (
          <Chip
            key={label}
            label={`${label} ${count}`}
            onClick={() => onFilter(type)}
            variant={active ? 'filled' : 'outlined'}
            color={active ? (type ? TYPE_COLOR[type] : 'primary') : 'default'}
            sx={{
              fontWeight: active ? 700 : 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
            }}
          />
        );
      })}
    </Stack>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const ReviewQueue: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading]         = useState(true);
  const [items, setItems]             = useState<ReviewQueueItem[]>([]);
  const [summary, setSummary]         = useState<ReviewQueueSummary>({
    total: 0, wine: 0, producer: 0, region: 0, grape: 0, wine_name: 0,
  });
  const [typeFilter, setTypeFilter]   = useState<ReviewEntityType | undefined>(undefined);
  const [page, setPage]               = useState(0);   // MUI 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalItems, setTotalItems]   = useState(0);
  const [actioningId, setActioningId] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reviewQueueService.getQueue(typeFilter, page + 1, rowsPerPage);
      setItems(data.items);
      setSummary(data.summary);
      setTotalItems(data.pagination.total);
    } catch (err) {
      console.error('Failed to fetch review queue:', err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, page, rowsPerPage]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleFilterChange = (t: ReviewEntityType | undefined) => {
    setTypeFilter(t);
    setPage(0);
  };

  const handleApprove = useCallback(async (item: ReviewQueueItem) => {
    setActioningId(item.id);
    try {
      await reviewQueueService.approve(item.type, item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setSummary(prev => ({ ...prev, [item.type]: prev[item.type] - 1, total: prev.total - 1 }));
      setTotalItems(prev => prev - 1);
    } catch (err) {
      console.error('Approve failed:', err);
    } finally {
      setActioningId(null);
    }
  }, []);

  const handleReject = useCallback(async (item: ReviewQueueItem) => {
    if (!window.confirm(`Reject and deactivate "${item.displayName}"?`)) return;
    setActioningId(item.id);
    try {
      await reviewQueueService.reject(item.type, item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setSummary(prev => ({ ...prev, [item.type]: prev[item.type] - 1, total: prev.total - 1 }));
      setTotalItems(prev => prev - 1);
    } catch (err) {
      console.error('Reject failed:', err);
    } finally {
      setActioningId(null);
    }
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Review Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Records created via import or auto-create that need manual review before going live.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchQueue} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Summary + filter chips */}
        <Box sx={{ mb: 3 }}>
          <SummaryBar summary={summary} activeFilter={typeFilter} onFilter={handleFilterChange} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No records pending review.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 120 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 130 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => {
                    const isActioning = actioningId === item.id;
                    const canReject   = REJECT_SUPPORTED.has(item.type);
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{
                          opacity: isActioning ? 0.5 : 1,
                          transition: 'opacity 0.2s',
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={REVIEW_TYPE_LABELS[item.type]}
                            size="small"
                            color={TYPE_COLOR[item.type]}
                            variant="outlined"
                            sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {item.displayName || '(unnamed)'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <KeyFields fields={item.keyFields} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Approve — mark as reviewed">
                              <span>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleApprove(item)}
                                  disabled={isActioning}
                                >
                                  <ApproveIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {canReject && (
                              <Tooltip title="Reject — deactivate record">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleReject(item)}
                                    disabled={isActioning}
                                  >
                                    <RejectIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Edit record">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(getEditUrl(item.type, item.id))}
                                disabled={isActioning}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ReviewQueue;
